import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { SecretService } from './secret.service';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private storeId: object = {};

  private profileImageSource = new BehaviorSubject<string | null>(null);
  profileImage$ = this.profileImageSource.asObservable();

  // ✅ BehaviorSubjects initialized with sessionStorage (if available)
  private projectNameSource = new BehaviorSubject<string | null>(
    sessionStorage.getItem('projectName'),
  );
  private companyNameSource = new BehaviorSubject<string | null>(
    sessionStorage.getItem('companyName'),
  );

  projectName$ = this.projectNameSource.asObservable();
  companyName$ = this.companyNameSource.asObservable();

  constructor(
    private router: Router,
    private secretService: SecretService,
  ) {}

  /**
   * ✅ Update and store project & company name
   * This will automatically update subscribers (like PublicLayoutComponent)
   */
  updateProjectCompany(project: string, company: string) {
    sessionStorage.setItem('projectName', project);
    sessionStorage.setItem('companyName', company);
    this.projectNameSource.next(project);
    this.companyNameSource.next(company);
  }

  // Store/retrieve any object globally
  setDeviceData(val: object) {
    this.storeId = val;
  }
  getDeviceData() {
    return this.storeId;
  }

  // Update profile image dynamically
  updateProfileImage(imageSrc: string | null) {
    this.profileImageSource.next(imageSrc);
  }

  // Generate a random password
  generatePass(passwordLength = 16): string {
    const lowerCaseLetters = 'abcdefghijklmnopqrstuvwxyz';
    const upperCaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_-,.';
    const characters = lowerCaseLetters + upperCaseLetters + numbers + symbols;

    const generatedPassword = [];
    for (let i = 0; i < passwordLength; i++) {
      const idx = Math.floor(Math.random() * characters.length);
      generatedPassword.push(characters[idx]);
    }
    return generatedPassword.join('');
  }

  // Role helper
  getRoleNumber(): number | null {
    try {
      const raw = localStorage.getItem('role_number');
      if (!raw) return null;
      try {
        const decrypted = this.secretService.decryptLocalStorageData(raw);
        if (decrypted && !isNaN(Number(decrypted))) {
          return Number(decrypted);
        }
      } catch {}
      if (!isNaN(Number(raw))) {
        return Number(raw);
      }
    } catch {}
    return null;
  }

  // ACL check helpers
  getActionItem(actionName: string): boolean {
    const roleNum = this.getRoleNumber();
    // Developer (1) and Super Admin (2) have full bypass access across the system
    if (roleNum === 1 || roleNum === 2) {
      return true;
    }

    let list: any[] = [];
    try {
      const raw = localStorage.getItem('aclDataTTCPMT');
      if (raw) list = JSON.parse(raw);
    } catch {}

    const subList = Array.isArray(list)
      ? list.flatMap((item: any) => item.submenu || [])
      : [];
    const url = this.router.url.trim();
    let subUrl = url.substring(url.indexOf('/', 1));
    if (subUrl.includes('?')) subUrl = subUrl.split('?')[0];
    const subMenu = subList.find((sub: any) => sub.url === subUrl);
    if (subMenu) {
      return !!subMenu?.action?.find((act: any) => act.name === actionName);
    }

    // Check top-level menus if not found in sub-menus (e.g. /projects, /dashboard)
    const topMenu = Array.isArray(list)
      ? list.find(
          (m: any) =>
            m.url === subUrl ||
            (m.url &&
              m.url !== '/' &&
              (subUrl === m.url || subUrl.startsWith(m.url + '/'))),
        )
      : null;
    if (topMenu && topMenu.action && Array.isArray(topMenu.action)) {
      return !!topMenu.action.find((act: any) => act.name === actionName);
    }

    // Fallback for top-level menus without a dedicated sub-menu entry (e.g. /projects):
    // Internal team members (Admin 3, Manager 4, Member 5) have access by default
    if (roleNum && roleNum < 10) {
      return true;
    }

    // External Client (15) and Guest (11): can View by default, but cannot Create or Delete projects
    if (roleNum === 15 || roleNum === 11) {
      return actionName === 'View';
    }

    return false;
  }

  getActionItemBySubmenu(submenuName: string, actionName: string): boolean {
    const roleNum = this.getRoleNumber();
    if (roleNum === 1 || roleNum === 2) {
      return true;
    }
    let list: any[] = [];
    try {
      const raw = localStorage.getItem('aclDataTTCPMT');
      if (raw) list = JSON.parse(raw);
    } catch {}
    const subList = Array.isArray(list)
      ? list.flatMap((item: any) => item.submenu || [])
      : [];
    const subMenu = subList.find((sub: any) => sub.name === submenuName);
    if (subMenu) {
      return !!subMenu?.action?.find((act: any) => act.name === actionName);
    }
    const topMenu = Array.isArray(list)
      ? list.find((m: any) => m.name === submenuName)
      : null;
    if (topMenu && topMenu.action && Array.isArray(topMenu.action)) {
      return !!topMenu.action.find((act: any) => act.name === actionName);
    }
    return roleNum ? roleNum < 10 : false;
  }

  /**
   * Added 14 Aug 2026. Does this role have `actionName` on the page at `url`?
   *
   * The two helpers above answer for the page you are ON, or for a sub-menu
   * looked up by its display name. This one takes the route, which is what you
   * have when deciding whether to offer a link somewhere ELSE — and unlike the
   * name, the route is what actually has to work.
   *
   * @param url        route as stored on the sub-menu, e.g. '/members/list'
   * @param actionName defaults to 'View' — the right question for a link
   *
   * Returns false rather than throwing when the ACL has not been cached yet, so
   * a missing cache hides a link instead of breaking the screen showing it.
   */
  hasRouteAccess(url: string, actionName: string = 'View'): boolean {
    const roleNum = this.getRoleNumber();
    if (roleNum === 1 || roleNum === 2) {
      return true;
    }
    try {
      const raw = localStorage.getItem('aclDataTTCPMT');
      if (!raw) return roleNum ? roleNum < 10 : false;
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return roleNum ? roleNum < 10 : false;
      const subList = list.flatMap((item: any) => item.submenu || []);
      const subMenu = subList.find((sub: any) => sub?.url === url);
      if (subMenu) {
        return !!subMenu?.action?.find((act: any) => act.name === actionName);
      }
      const topMenu = list.find(
        (m: any) =>
          m.url === url ||
          (m.url &&
            m.url !== '/' &&
            (url === m.url || url.startsWith(m.url + '/'))),
      );
      if (topMenu && topMenu.action && Array.isArray(topMenu.action)) {
        return !!topMenu.action.find((act: any) => act.name === actionName);
      }
      return roleNum ? roleNum < 10 : false;
    } catch {
      return false;
    }
  }

  /**
   * Added 29 Oct 2026. Strict version of hasRouteAccess, for hiding a page's
   * Create / Edit / Delete buttons by User Privileges > Role Privilege.
   *
   * The helpers above fall back to "allowed" for internal roles when the menu
   * is not in the role's ACL, which is also what happens when every box on
   * that menu is unticked. This one answers false instead, so unticking an
   * action always hides it. Developer and Super Admin are always allowed.
   *
   * @param menuUrl url of the menu or sub-menu, e.g. '/full-calender', '/reports'
   */
  hasMenuAction(menuUrl: string, actionName: string): boolean {
    const roleNum = this.getRoleNumber();
    if (roleNum === 1 || roleNum === 2) {
      return true;
    }
    try {
      const raw = localStorage.getItem('aclDataTTCPMT');
      const list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return false;
      const subMenu = list
        .flatMap((item: any) => item.submenu || [])
        .find((sub: any) => sub?.url === menuUrl);
      const menu = subMenu || list.find((m: any) => m?.url === menuUrl);
      return !!menu?.action?.find((act: any) => act.name === actionName);
    } catch {
      return false;
    }
  }

  // Helper: list of months
  getMonths() {
    return [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sept',
      'Oct',
      'Nov',
      'Dec',
    ];
  }

  DATE_FORMAT: string = 'DD/MM/YYYY';

  formatDate(date: Date) {
    try {
      const m = moment(date);
      return m.isValid() ? m.format(this.DATE_FORMAT) : '';
    } catch {
      return '';
    }
  }
}
